import { baseQueryConfig } from "@/hooks/base"
import { actionSearchContacts } from "@/actions/dynamo.action"
import type { TContact } from "@/schemas/contacts"
import { useQuery } from "@tanstack/react-query"

interface IUseSearchContactsQueryProps {
  beginsWith?: string
  type?: "email" | "sms" | "all"
  refineFn?: (data: Array<TContact>) => Array<TContact>
  onSuccess?: (data: Array<TContact>) => void
  isDisabled?: boolean
}

export function getSearchContactsQueryKey({ beginsWith, type }: Pick<IUseSearchContactsQueryProps, "beginsWith" | "type">) {
  return ["search-contacts", beginsWith?.trim() ?? "", type ?? "all"] as const
}

export function useSearchContacts({ beginsWith, isDisabled, type = "all", refineFn, onSuccess }: IUseSearchContactsQueryProps) {
  const queryHook = useQuery({
    ...baseQueryConfig,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- callbacks transform/consume the response but do not identify the upstream search request
    queryKey: getSearchContactsQueryKey({ beginsWith, type }),
    queryFn: async () => {
      const queryResponse = (await actionSearchContacts({
        searchText: beginsWith ?? "",
        type,
      })) satisfies TContact[]

      let returnData = queryResponse
      if (refineFn) {
        returnData = refineFn(queryResponse)
      }

      if (onSuccess) {
        onSuccess(returnData)
      }

      return returnData
    },
    enabled: !isDisabled && !!beginsWith && beginsWith.trim().length > 0,
  })

  return [queryHook.data, queryHook] as const
}
